import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/StatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, File, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ApplicationDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [notes, setNotes] = useState('');
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/applications/${appId}`).then(r => setApp(r.data)).catch(() => navigate(-1));
    api.get(`/documents/${appId}`).then(r => setDocs(r.data)).catch(() => {});
  }, [appId, navigate]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await api.post(`/applications/${appId}/${action}`, { notes });
      toast.success(action === 'approve' ? 'Dossier approuve' : action === 'reject' ? 'Dossier rejete' : 'Correction demandee');
      setDialog(null);
      const r = await api.get(`/applications/${appId}`);
      setApp(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!app) return null;

  const info = app.personal_info || {};
  const canReview = ['submitted'].includes(app.status);

  const fields = [
    ['Nom complet', info.full_name], ['Date de naissance', info.date_of_birth],
    ['Lieu de naissance', info.place_of_birth], ['Genre', info.gender === 'M' ? 'Masculin' : 'Feminin'],
    ['Nationalite', info.nationality], ['N Passeport', info.passport_number],
    ['Pays de residence', info.country_of_residence], ['Ville', info.city_of_residence],
    ['Adresse', info.address], ['Telephone', info.phone],
    ['Email', info.email], ['Profession', info.profession],
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="application-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{info.full_name || 'Dossier'}</h1>
        </div>
        <StatusBadge status={app.status} />
      </div>

      <Card className="border">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Informations personnelles</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-sm font-medium">{value || '-'}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Documents ({docs.length})</CardTitle></CardHeader>
        <CardContent>
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun document</p>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <div key={doc.document_id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <File className="w-5 h-5 text-[#007FFF]" />
                    <div>
                      <p className="text-sm font-medium">{doc.file_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{doc.type}</p>
                    </div>
                  </div>
                  <a href={`${BACKEND}${doc.file_path}`} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${doc.document_id}`}>
                    <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {canReview && (
        <div className="flex gap-3">
          <Button className="flex-1 bg-[#009543] hover:bg-[#007A36]" onClick={() => { setNotes(''); setDialog('approve'); }} data-testid="approve-btn">
            <CheckCircle className="w-4 h-4 mr-2" /> Approuver
          </Button>
          <Button className="flex-1 bg-[#CE1021] hover:bg-[#B00E1C]" onClick={() => { setNotes(''); setDialog('reject'); }} data-testid="reject-btn">
            <XCircle className="w-4 h-4 mr-2" /> Rejeter
          </Button>
          <Button className="flex-1" variant="outline" onClick={() => { setNotes(''); setDialog('request-correction'); }} data-testid="correction-btn">
            <AlertTriangle className="w-4 h-4 mr-2" /> Correction
          </Button>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {dialog === 'approve' ? 'Approuver le dossier' : dialog === 'reject' ? 'Rejeter le dossier' : 'Demander une correction'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Notes {dialog !== 'approve' ? '(obligatoire)' : '(optionnel)'}</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ajoutez vos remarques..." rows={4} data-testid="action-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annuler</Button>
            <Button
              onClick={() => handleAction(dialog)}
              disabled={loading || (dialog !== 'approve' && !notes.trim())}
              className={dialog === 'approve' ? 'bg-[#009543] hover:bg-[#007A36]' : dialog === 'reject' ? 'bg-[#CE1021] hover:bg-[#B00E1C]' : ''}
              data-testid="confirm-action-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
